import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Header.css';
import HonorairesModal from './HonorairesModal';
import DepensesModal from './DepensesModal';
import DepensesFilterModal from './DepensesFilterModal';
import AllHonorairesModal from './AllHonorairesModal';
import AllDepensesModal from './AllDepensesModal';
import AllClientsModal from './AllClientsModal';
import AllBeneficiairesModal from './AllBeneficiairesModal';
import EtatCgmModal from './EtatCgmModal';
import RecuTemplate from './RecuTemplate';
import UserManagementModal from './UserManagementModal';
import NavPageLink from './NavPageLink';
import { authJsonHeaders } from '../utils/authHeaders';

export default function Header() {
  const { user, isAuthenticated, logout, isAdmin, isSuperAdmin, activeCabinet } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showHonorairesDropdown, setShowHonorairesDropdown] = useState(false);
  const [showDepensesDropdown, setShowDepensesDropdown] = useState(false);
  const [showHonorairesModal, setShowHonorairesModal] = useState(false);
  const [honorairesType, setHonorairesType] = useState('');
  const [showDepensesModal, setShowDepensesModal] = useState(false);
  const [depensesAction, setDepensesAction] = useState('');
  const [showDepensesFilterModal, setShowDepensesFilterModal] = useState(false);
  const [depensesFilterType, setDepensesFilterType] = useState('');
  const [showDepensesClientDropdown, setShowDepensesClientDropdown] = useState(false);
  const [showDepensesClientDetailDropdown, setShowDepensesClientDetailDropdown] = useState(false);
  const [showAllHonorairesModal, setShowAllHonorairesModal] = useState(false);
  const [showAllDepensesModal, setShowAllDepensesModal] = useState(false);
  const [depensesActionMode, setDepensesActionMode] = useState('view');
  const [showRecuTemplate, setShowRecuTemplate] = useState(false);
  const [recuData, setRecuData] = useState(null);
  const [showAllClientsModal, setShowAllClientsModal] = useState(false);
  const [showAllBeneficiairesModal, setShowAllBeneficiairesModal] = useState(false);
  const [showEtatCgmDropdown, setShowEtatCgmDropdown] = useState(false);
  const [showCgmDropdown, setShowCgmDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showEtatClientDropdown, setShowEtatClientDropdown] = useState(false);
  const [showEtatCgmModal, setShowEtatCgmModal] = useState(false);
  const [etatCgmFilteredData, setEtatCgmFilteredData] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [caisseTotal, setCaisseTotal] = useState(0);
  const [caisseLoading, setCaisseLoading] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showUserSettingsPanel, setShowUserSettingsPanel] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(() => localStorage.getItem('gestionCAIS_profile_photo') || '');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('gestionCAIS_dark_mode') === 'true');
  const profileInputRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isClientDetailPage = location.pathname.includes('/client/') && (
    location.pathname.includes('/charges') ||
    location.pathname.includes('/etat/') ||
    location.pathname.includes('/depenses/') ||
    location.pathname.includes('/honoraires/')
  );

  /** Page d'accueil : choix du module (Gestion Client CGM / Gestion NH) */
  const isHomeModulePage = location.pathname === '/';

  /** Module Gestion NH — navbar simplifiée (sans Client / CGM / Caisse / Carte / Stats) */
  const isNhModulePage =
    location.pathname === '/nh' || location.pathname.startsWith('/nh/');

  /** Module Gestion de caisse CGM (toutes les pages sauf accueil et NH) */
  const isCgmCaisseModulePage = !isHomeModulePage && !isNhModulePage;
  const cgmNavLabel = activeCabinet?.is_default || Number(activeCabinet?.id) === 1 ? 'CGM1' : 'CGM2';
  const canAccessArchive = String(user?.username || '').trim().toLowerCase() === 'amen';


  // Appliquer/retirer le mode sombre
  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('gestionCAIS_dark_mode', darkMode);
  }, [darkMode]);

  const handleProfilePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('La photo ne doit pas dépasser 2 Mo.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setProfilePhoto(dataUrl);
      localStorage.setItem('gestionCAIS_profile_photo', dataUrl);
    };
    reader.readAsDataURL(file);
  };

  // Fermer les panneaux / menus déroulants quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotificationPanel && !event.target.closest('.notification-container')) {
        setShowNotificationPanel(false);
      }
      if (showAdminPanel && !event.target.closest('.admin-menu-container')) {
        setShowAdminPanel(false);
      }
      if (showUserSettingsPanel && !event.target.closest('.user-settings-container')) {
        setShowUserSettingsPanel(false);
      }
      if (showClientDropdown && !event.target.closest('[data-nav-menu="client"]')) {
        setShowClientDropdown(false);
      }
      if (showCgmDropdown && !event.target.closest('[data-nav-menu="cgm"]')) {
        setShowCgmDropdown(false);
      }
    };

    if (
      showNotificationPanel ||
      showAdminPanel ||
      showUserSettingsPanel ||
      showClientDropdown ||
      showCgmDropdown
    ) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [
    showNotificationPanel,
    showAdminPanel,
    showUserSettingsPanel,
    showClientDropdown,
    showCgmDropdown,
  ]);

  // Fonction de déconnexion
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Récupérer le total de la caisse CGM (somme des honoraires reçus)
  const fetchCaisseTotal = async () => {
    if (!isAuthenticated) return;

    setCaisseLoading(true);
    try {
      const response = await fetch('/api/honoraires/caisse-live', {
        headers: authJsonHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        const newTotal = data.total_caisse || 0;
        console.log('Caisse CGM mise à jour:', newTotal);
        setCaisseTotal(newTotal);
      } else {
        console.error('Erreur dans la réponse de la caisse:', data);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération de la caisse:', err);
    } finally {
      setCaisseLoading(false);
    }
  };

  // Charger la caisse au montage et périodiquement
  useEffect(() => {
    if (!isAuthenticated) return;

    fetchCaisseTotal();
    // Mettre à jour toutes les 1 minute (60 secondes)
    const interval = setInterval(fetchCaisseTotal, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeCabinet?.id]);

  // Écouter les événements de mise à jour de la caisse
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleCaisseUpdate = () => {
      console.log('Événement caisse-updated reçu dans Header');
      // Petit délai pour s'assurer que la base de données est à jour
      setTimeout(() => {
        console.log('Appel de fetchCaisseTotal()...');
        fetchCaisseTotal();
      }, 200);
    };

    window.addEventListener('caisse-updated', handleCaisseUpdate);
    window.addEventListener('charge-added', handleCaisseUpdate);
    window.addEventListener('charge-updated', handleCaisseUpdate);
    window.addEventListener('charge-deleted', handleCaisseUpdate);

    return () => {
      window.removeEventListener('caisse-updated', handleCaisseUpdate);
      window.removeEventListener('charge-added', handleCaisseUpdate);
      window.removeEventListener('charge-updated', handleCaisseUpdate);
      window.removeEventListener('charge-deleted', handleCaisseUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Récupérer les notifications (uniquement pour les admins)
  const fetchNotifications = async () => {
    if (!isAuthenticated || user?.role !== 'admin') return;

    try {
      const response = await fetch('/api/notifications', { headers: authJsonHeaders() });
      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications || []);
        const unread = (data.notifications || []).filter(n => !n.is_read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des notifications:', err);
    }
  };

  // Charger les notifications au montage et périodiquement
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') return;

    fetchNotifications();
    // Mettre à jour toutes les 5 secondes
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.role]);

  // Écouter les événements de nouvelle notification
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') return;

    const handleNewNotification = () => {
      setTimeout(() => {
        fetchNotifications();
      }, 500);
    };

    window.addEventListener('new-notification', handleNewNotification);
    return () => {
      window.removeEventListener('new-notification', handleNewNotification);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.role]);

  // Ouvrir le gabarit de reçu via un événement global
  useEffect(() => {
    const openRecu = (event) => {
      setRecuData(event.detail || null);
      setShowRecuTemplate(true);
    };
    window.addEventListener('open-recu-template', openRecu);
    return () => window.removeEventListener('open-recu-template', openRecu);
  }, []);

  // Ouvrir le modal Dépenses depuis la page client (bouton "Dépense")
  useEffect(() => {
    const openDepensesModal = () => {
      setDepensesAction('ajouter');
      setShowDepensesModal(true);
    };
    window.addEventListener('open-depenses-modal', openDepensesModal);
    return () => window.removeEventListener('open-depenses-modal', openDepensesModal);
  }, []);

  // Ouvrir les états CGM filtrés via un événement global
  useEffect(() => {
    const openEtatCgmFiltered = (event) => {
      setEtatCgmFilteredData(event.detail || null);
      setShowEtatCgmModal(true);
    };

    const openEtatBureauForBeneficiaire = (event) => {
      setEtatCgmFilteredData(event.detail || null);
      setShowEtatCgmModal(true);
    };

    window.addEventListener('open-etat-cgm-filtered', openEtatCgmFiltered);
    window.addEventListener('open-etat-bureau-for-beneficiaire', openEtatBureauForBeneficiaire);
    return () => {
      window.removeEventListener('open-etat-cgm-filtered', openEtatCgmFiltered);
      window.removeEventListener('open-etat-bureau-for-beneficiaire', openEtatBureauForBeneficiaire);
    };
  }, []);


  const handleClientSearch = () => {
    setShowDropdown(false);
    // Attendre un peu pour que la transition se termine
    setTimeout(() => {
      navigate('/search-client');
    }, 200);
  };




  const handleHonorairesClick = (type) => {
    // Fermer le dropdown d'abord
    setShowHonorairesDropdown(false);

    // Attendre un peu pour que la transition se termine
    setTimeout(() => {
      // Navigation par routes selon le type
      if (isClientDetailPage) {
        // Rester dans le contexte client (navbar client) et utiliser les routes client
        const clientId = location.pathname.split('/')[2];
        switch (type) {
          case 'jour':
            navigate(`/client/${clientId}/honoraires/jour`);
            break;
          case 'mois':
            navigate(`/client/${clientId}/honoraires/mois`);
            break;
          case 'annee':
            navigate(`/client/${clientId}/honoraires/annee`);
            break;
          case 'toutes':
            navigate(`/client/${clientId}/honoraires/tous`);
            break;
          default:
            break;
        }
      } else {
        switch (type) {
          case 'jour':
            navigate('/honoraires/jour');
            break;
          case 'mois':
            navigate('/honoraires/mois');
            break;
          case 'annee':
            navigate('/honoraires/annee');
            break;
          case 'toutes':
            navigate('/honoraires/tous');
            break;
          default:
            break;
        }
      }
    }, 200);

    // Si on est dans une page de détails client, passer l'ID du client
    if (isClientDetailPage) {
      const clientId = location.pathname.split('/')[2];
      window.currentHonorairesClientId = clientId;
    } else {
      // Si on est sur la page d'accueil, réinitialiser l'ID du client
      window.currentHonorairesClientId = null;
    }
  };

  const handleDepensesClick = (action) => {
    console.log(`Action Dépenses: ${action}`);

    // Définir les variables globales selon le contexte
    if (isClientDetailPage) {
      setShowDepensesClientDetailDropdown(false);
      // Si on est dans une page de détails client, passer l'ID du client
      const clientId = location.pathname.split('/')[2];
      window.currentDepensesClientId = clientId;
      window.currentDepensesType = 'client';
    } else {
      setShowDepensesDropdown(false); // Pour Dépenses Bureau
      window.currentDepensesClientId = null;
      window.currentDepensesType = 'bureau';
    }

    // Attendre un peu pour que la transition se termine
    setTimeout(() => {
      setDepensesAction(action);

      if (action === 'ajouter') {
        setShowDepensesModal(true);
      } else if (action === 'modifier') {
        // Afficher le modal de toutes les dépenses pour modification
        setDepensesActionMode('modify');
        setShowAllDepensesModal(true);
      } else if (action === 'supprimer') {
        // Afficher le modal de toutes les dépenses pour suppression
        setDepensesActionMode('delete');
        setShowAllDepensesModal(true);
      } else if (action === 'voir-toutes') {
        if (isClientDetailPage) {
          // Naviguer vers la page dédiée client
          const clientId = location.pathname.split('/')[2];
          navigate(`/client/${clientId}/depenses/toutes`);
        } else {
          // Si on est sur la page d'accueil (Dépenses Bureau), naviguer vers la route
          navigate('/depenses-bureau/toutes');
        }
      }
    }, 200);
  };

  const handleAddDepenseClick = () => {
    console.log('Ajouter une nouvelle dépense CGM');
    setShowCgmDropdown(false);
    navigate('/depenses-cgm/ajouter');
  };

  const handleDepensesFilterClick = (type) => {
    // Fermer le dropdown d'abord
    setShowDepensesDropdown(false);

    // Attendre un peu pour que la transition se termine
    setTimeout(() => {
      // Vérifier si on est dans les détails d'un client
      if (isClientDetailPage) {
        // Si on est dans les détails d'un client, naviguer vers les routes client
        const clientId = location.pathname.split('/')[2];
        switch (type) {
          case 'jour':
            navigate(`/client/${clientId}/depenses/jour`);
            break;
          case 'mois':
            navigate(`/client/${clientId}/depenses/mois`);
            break;
          case 'annee':
            navigate(`/client/${clientId}/depenses/annee`);
            break;
          default:
            break;
        }
      } else {
        // Si on est sur la page d'accueil, naviguer vers les routes bureau
        switch (type) {
          case 'jour':
            navigate('/depenses-bureau/jour');
            break;
          case 'mois':
            navigate('/depenses-bureau/mois');
            break;
          case 'annee':
            navigate('/depenses-bureau/annee');
            break;
          default:
            break;
        }
      }
    }, 200);
  };

  const handleDepensesBeneficiaireClick = () => {
    setShowDepensesDropdown(false);

    // Attendre un peu pour que la transition se termine
    setTimeout(() => {
      // Vérifier si on est dans les détails d'un client
      if (isClientDetailPage) {
        // Si on est dans les détails d'un client, naviguer vers la route client
        const clientId = location.pathname.split('/')[2];
        navigate(`/client/${clientId}/depenses/beneficiaire`);
      } else {
        // Si on est sur la page d'accueil, naviguer vers la route bureau
        navigate('/depenses-bureau/beneficiaire');
      }
    }, 200);
  };

  // Gestionnaires spécifiques pour les boutons admin
  const handleAdminDepensesClientFilterClick = (type) => {
    setShowDepensesClientDropdown(false);

    // Attendre un peu pour que la transition se termine
    setTimeout(() => {
      // Navigation par routes selon le type pour Dépenses Client
      switch (type) {
        case 'jour':
          navigate('/depenses-client/jour');
          break;
        case 'mois':
          navigate('/depenses-client/mois');
          break;
        case 'annee':
          navigate('/depenses-client/annee');
          break;
        default:
          break;
      }
    }, 200);
  };

  const handleAdminDepensesClientClick = (action) => {
    if (action === 'voir-toutes') {
      setShowDepensesClientDropdown(false);

      // Aller vers la page dédiée (toutes dépenses client)
      setTimeout(() => {
        navigate('/depenses-client/toutes');
      }, 200);
    }
  };

  const handleAdminEtatClientFilterClick = (type) => {
    setShowEtatClientDropdown(false);

    setTimeout(() => {
      // Routes globales États Client (tous clients)
      switch (type) {
        case 'client': navigate('/etat-client/client'); break;
        case 'jour': navigate('/etat-client/jour'); break;
        case 'mois': navigate('/etat-client/mois'); break;
        case 'annee': navigate('/etat-client/annee'); break;
        case 'tous': navigate('/etat-client/tous'); break;
        default: break;
      }
    }, 200);
  };

  const handleClientDetailEtatFilterClick = (type) => {
    // Récupérer l'ID du client depuis l'URL actuelle
    const currentPath = location.pathname;
    const clientIdMatch = currentPath.match(/\/client\/(\d+)/);

    if (clientIdMatch) {
      const clientId = clientIdMatch[1];

      setTimeout(() => {
        // Routes spécifiques au client pour les états
        switch (type) {
          case 'jour': navigate(`/client/${clientId}/etat/jour`); break;
          case 'mois': navigate(`/client/${clientId}/etat/mois`); break;
          case 'annee': navigate(`/client/${clientId}/etat/annee`); break;
          default: break;
        }
      }, 200);
    }
  };

  const handleAdminEtatClientClick = (action) => {
    if (action === 'voir-toutes') {
      setShowEtatClientDropdown(false);

      setTimeout(() => {
        navigate('/etat-client/tous');
      }, 200);
    }
  };


  const handleAfficherParJour = () => {
    setShowEtatCgmDropdown(false);

    // Attendre un peu pour que la transition se termine
    setTimeout(() => {
      navigate('/etat-bureau/jour');
    }, 200);
  };


  const handleAfficherParMois = () => {
    setShowEtatCgmDropdown(false);

    // Attendre un peu pour que la transition se termine
    setTimeout(() => {
      navigate('/etat-bureau/mois');
    }, 200);
  };

  const handleAfficherParAnnee = () => {
    setShowEtatCgmDropdown(false);
    setShowCgmDropdown(false);

    setTimeout(() => {
      navigate('/etat-bureau/toutes');
    }, 200);
  };

  const handleFondClientChezCgm = () => {
    setShowCgmDropdown(false);

    // Attendre un peu pour que la transition se termine
    setTimeout(() => {
      navigate('/fond-client-cgm');
    }, 200);
  };

  const handleVoirToutesLesEtats = () => {
    setShowEtatCgmDropdown(false);

    // Si on est sur la page d'accueil (Etat Bureau), naviguer vers la route
    if (!isClientDetailPage) {
      // Attendre un peu pour que la transition se termine
      setTimeout(() => {
        navigate('/etat-bureau/toutes');
      }, 200);
    } else {
      // Si on est dans une page de détails client, naviguer vers la route client
      setTimeout(() => {
        const clientId = location.pathname.split('/')[2];
        navigate(`/client/${clientId}/etat/tous`);
      }, 200);
    }
  };

  const closeAdminPanel = () => setShowAdminPanel(false);

  const goAdminRoute = (path) => {
    closeAdminPanel();
    setShowMobileMenu(false);
    navigate(path);
  };

  const renderAdminMenuPanel = (extraDropdownClass = '') => {
    if (!showAdminPanel) return null;
    const dropdownClass = ['admin-uiverse-dropdown', extraDropdownClass].filter(Boolean).join(' ');
    return (
      <>
        <div className="notification-overlay" onClick={closeAdminPanel} role="presentation" />
        <div className={dropdownClass}>
          <div className="admin-uiverse-card">
            <ul className="admin-uiverse-list">
              <li
                className="admin-uiverse-element"
                role="button"
                tabIndex={0}
                onClick={() => goAdminRoute('/comptes')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goAdminRoute('/comptes');
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7e8590"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <p className="admin-uiverse-label">Gérer les comptes</p>
              </li>
              <li
                className="admin-uiverse-element"
                role="button"
                tabIndex={0}
                onClick={() => goAdminRoute('/dashboard')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goAdminRoute('/dashboard');
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7e8590"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 3v18h18" />
                  <path d="m19 9-5 5-4-4-3 3" />
                </svg>
                <p className="admin-uiverse-label">Statistiques</p>
              </li>
            </ul>
          </div>
        </div>
      </>
    );
  };


  return (
    <>
      <style jsx>{`
        
        .dropdown {
          position: relative;
          display: inline-block;
        }
        
        /* Liste reliée au bouton (touche le bord bas, alignée à gauche) */
        .modern-dropdown {
          font-family: 'Poppins', 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
          background: #ffffff;
          border: 1px solid #d0d5dd;
          border-radius: 0 10px 10px 10px;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.1);
          margin-top: 0 !important;
          margin-bottom: 0;
          opacity: 0;
          transform: translateY(-4px);
          transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease;
          pointer-events: none;
          position: absolute;
          z-index: 9999;
          width: max(100%, 240px);
          left: 0;
          top: calc(100% - 1px);
          padding: 6px;
          visibility: hidden;
          box-sizing: border-box;
          min-width: 240px;
          list-style: none;
        }
        
        .modern-dropdown.show {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
          visibility: visible;
          display: block !important;
        }

        .uiverse-nav-dropdown {
          position: relative;
          z-index: 1002;
          display: inline-flex;
          flex-direction: column;
          align-items: stretch;
        }

        .uiverse-nav-dropdown > .uiverse-nav-btn.dropdown-toggle {
          border: 1px solid transparent;
          background: transparent;
          border-radius: 10px;
          position: relative;
          z-index: 2;
          transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease,
            border-radius 0.15s ease, box-shadow 0.2s ease;
        }

        .uiverse-nav-dropdown.is-open > .uiverse-nav-btn.dropdown-toggle,
        .uiverse-nav-dropdown:hover > .uiverse-nav-btn.dropdown-toggle {
          background: #ffffff;
          border-color: #d0d5dd;
          color: #0f172a;
          box-shadow: none;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          border-bottom-color: #ffffff;
        }

        .uiverse-nav-dropdown.is-open > .uiverse-nav-btn.dropdown-toggle:hover,
        .uiverse-nav-dropdown:hover > .uiverse-nav-btn.dropdown-toggle:hover {
          background: #ffffff;
          color: #0f172a;
          box-shadow: none;
        }

        .uiverse-nav-dropdown.is-open > .uiverse-nav-btn.dropdown-toggle i:not(.nav-btn-chevron),
        .uiverse-nav-dropdown:hover > .uiverse-nav-btn.dropdown-toggle i:not(.nav-btn-chevron) {
          color: #64748b;
          opacity: 1;
        }

        .uiverse-nav-dropdown > .uiverse-nav-btn .nav-btn-chevron {
          margin-left: 0.4em;
          font-size: 0.62rem;
          opacity: 0.7;
          transition: transform 0.2s ease;
        }

        .uiverse-nav-dropdown.is-open > .uiverse-nav-btn .nav-btn-chevron,
        .uiverse-nav-dropdown:hover > .uiverse-nav-btn .nav-btn-chevron {
          transform: rotate(180deg);
          opacity: 1;
          color: inherit;
        }

        .uiverse-nav-dropdown > .uiverse-nav-btn.dropdown-toggle::after {
          display: none !important;
          content: none !important;
        }
        
        .modern-dropdown-item {
          color: #334155;
          padding: 10px 12px;
          border-radius: 8px;
          margin: 1px 0;
          transition: background 0.15s ease, color 0.15s ease;
          font-weight: 500;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          background: transparent;
          border: none;
          text-transform: none;
          letter-spacing: 0.01em;
          width: 100%;
          text-align: left;
        }
        
        .modern-dropdown:not(.show) .modern-dropdown-item {
          opacity: 0;
        }
        
        .modern-dropdown.show .modern-dropdown-item {
          opacity: 1;
          animation: none;
        }
        
        .modern-dropdown-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 20%;
          bottom: 20%;
          width: 3px;
          background: #2563eb;
          border-radius: 3px;
          transform: scaleY(0);
          transition: transform 0.15s ease;
        }
        
        .modern-dropdown-item:hover {
          background: #f1f5f9;
          color: #0f172a;
          transform: none;
        }
        
        .modern-dropdown-item:hover::before {
          transform: scaleY(1);
        }
        
        .modern-dropdown-item:active {
          background: #e2e8f0;
          transform: none;
        }
        
        .modern-dropdown-item i {
          font-size: 0.9rem;
          width: 22px;
          margin-right: 10px;
          color: #64748b;
          transition: color 0.15s ease;
        }
        
        .modern-dropdown-item:hover i {
          transform: none;
          color: #2563eb;
        }
        
        .modern-dropdown-header {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          border-radius: 8px;
          margin: 2px 2px 4px;
          padding: 8px 12px;
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          box-shadow: none;
          border: 1px solid #e2e8f0;
          text-shadow: none;
          opacity: 1;
          transform: none;
        }
        
        .modern-dropdown.show .modern-dropdown-header {
          opacity: 1;
          transform: none;
        }
        
        .modern-dropdown-header i {
          margin-right: 8px;
          font-size: 0.85rem;
          color: #64748b;
        }
        
        .modern-dropdown .dropdown-divider {
          margin: 4px 8px;
          border-top: 1px solid #e2e8f0;
          opacity: 1;
        }
        
        .modern-user-info {
          background: transparent;
          border-radius: 6px;
          padding: 6px 14px;
          border: none;
          color: #555;
          font-weight: 500;
          font-size: 0.9rem;
        }
        
        .modern-user-text {
          color: #555;
          font-weight: 500;
          font-size: 0.9rem;
        }
        
        
        .mobile-menu-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(4px);
          z-index: 9998;
          animation: fadeIn 0.3s ease;
        }
        
        .mobile-menu-overlay.show {
          display: block;
        }
        
        .mobile-menu {
          display: none;
          position: fixed;
          top: 0;
          right: -100%;
          width: min(88vw, 360px);
          height: 100%;
          height: 100dvh;
          background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 40%, #ffffff 100%);
          box-shadow: -12px 0 40px rgba(19, 78, 74, 0.28);
          z-index: 9999;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          transition: right 0.32s cubic-bezier(0.22, 1, 0.36, 1);
          padding: 0;
          font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
        }
        
        .mobile-menu.show {
          right: 0;
        }
        
        .mobile-menu-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          padding: 1.1rem 1.15rem;
          background: linear-gradient(125deg, #134e4a 0%, #0f766e 48%, #3b82f6 100%);
          color: #fff;
          border-bottom: none;
          box-shadow: 0 8px 24px -12px rgba(19, 78, 74, 0.55);
        }
        
        .mobile-menu-title {
          font-weight: 700;
          font-size: 1.05rem;
          color: #ffffff;
          letter-spacing: 0.01em;
          display: flex;
          align-items: center;
          gap: 0.55rem;
        }

        .mobile-menu-title img {
          width: 32px;
          height: 32px;
          border-radius: 9px;
          background: #fff;
          padding: 3px;
          object-fit: contain;
        }
        
        .mobile-menu-close {
          background: rgba(255, 255, 255, 0.16);
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 10px;
          font-size: 1.2rem;
          color: #fff;
          cursor: pointer;
          padding: 6px 10px;
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          line-height: 1;
          flex-shrink: 0;
        }
        
        .mobile-menu-close span {
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1;
          display: block;
        }
        
        .mobile-menu-close:hover {
          background: rgba(255, 255, 255, 0.28);
        }
        
        .mobile-menu-content {
          padding: 1rem 0.85rem 1.5rem;
        }

        .mobile-menu-user {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.85rem 0.95rem;
          margin-bottom: 1rem;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid rgba(15, 118, 110, 0.16);
          box-shadow: 0 8px 20px -14px rgba(19, 78, 74, 0.35);
        }

        .mobile-menu-user__avatar {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: grid;
          place-items: center;
          background: linear-gradient(135deg, #0f766e, #3b82f6);
          color: #fff;
          font-weight: 800;
          font-size: 0.95rem;
          flex-shrink: 0;
        }

        .mobile-menu-user__meta {
          min-width: 0;
          flex: 1;
        }

        .mobile-menu-user__name {
          display: block;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          line-height: 1.2;
        }

        .mobile-menu-user__cab {
          display: block;
          margin-top: 0.2rem;
          font-size: 0.78rem;
          color: #64748b;
          font-weight: 500;
        }
        
        .mobile-menu-section {
          margin-bottom: 1.05rem;
        }
        
        .mobile-menu-section-title {
          font-weight: 800;
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: #0f766e;
          background: rgba(15, 118, 110, 0.1);
          border: 1px solid rgba(15, 118, 110, 0.18);
          padding: 0.45rem 0.75rem;
          margin-bottom: 0.55rem;
          border-radius: 999px;
          display: inline-block;
        }
        
        .mobile-menu-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 13px 16px;
          margin-bottom: 8px;
          background: #ffffff;
          border: 1px solid rgba(15, 118, 110, 0.14);
          border-radius: 14px;
          color: #0f172a;
          text-align: left;
          font-weight: 600;
          font-size: 0.92rem;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          box-shadow: 0 6px 16px -12px rgba(19, 78, 74, 0.35);
        }
        
        .mobile-menu-item:hover,
        .mobile-menu-item:active {
          background: #f0fdfa;
          color: #134e4a;
          padding-left: 18px;
          border-color: rgba(15, 118, 110, 0.35);
          box-shadow: inset 3px 0 0 #0f766e;
        }
        
        .mobile-menu-item i {
          margin-right: 12px;
          width: 20px;
          text-align: center;
          font-size: 1rem;
          color: #0f766e;
        }

        .mobile-menu-item:hover i,
        .mobile-menu-item:active i {
          color: #134e4a;
        }

        .mobile-menu-item--danger {
          color: #b91c1c;
          border-color: rgba(220, 38, 38, 0.22);
          background: #fff5f5;
        }

        .mobile-menu-item--danger i {
          color: #dc2626;
        }

        .mobile-menu-item--danger:hover,
        .mobile-menu-item--danger:active {
          background: #fee2e2;
          color: #991b1b;
          box-shadow: inset 3px 0 0 #dc2626;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .uiverse-nav-dropdown {
          position: relative;
          z-index: 1002;
        }
        
        /* Styles pour le conteneur de notification */
        .notification-container {
          position: relative;
          display: inline-block;
        }
        
        .notification-trigger {
          position: relative;
          cursor: pointer;
          padding: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s ease;
        }
        
        .notification-trigger:hover {
          transform: scale(1.1);
        }
        
        .notification-icon {
          font-size: 1.3rem;
          display: inline-block;
          line-height: 1;
        }
        
        .notification-badge {
          position: absolute;
          top: 2px;
          right: 2px;
          background: linear-gradient(135deg, #e2a832 0%, #daa520 100%);
          color: #1e293b;
          border-radius: 50%;
          min-width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.65rem;
          font-weight: 700;
          border: 2px solid #ffffff;
          box-shadow: 0 1px 4px rgba(218, 165, 32, 0.45);
          padding: 0 4px;
        }
        
        /* Overlay pour fermer la fenêtre */
        .notification-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9998;
          background: transparent;
        }
        
        /* Fenêtre de notification — moderne, alignée charte CGM */
        .notification-panel {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 360px;
          max-width: 92vw;
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12);
          z-index: 9999;
          overflow: hidden;
          animation: notifSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          text-transform: none;
        }

        @keyframes notifSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .notification-panel-header {
          background: #ffffff;
          color: #0f172a;
          padding: 0.9rem 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #e2e8f0;
        }

        .notification-panel-header h6 {
          margin: 0;
          font-weight: 600;
          font-size: 0.9rem;
          letter-spacing: 0.01em;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #0f172a;
        }

        .notification-panel-header h6 i {
          color: #0f766e;
          font-size: 0.85rem;
        }

        .notification-panel-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          margin-left: 0.35rem;
          border-radius: 999px;
          background: linear-gradient(135deg, #e2a832 0%, #daa520 100%);
          color: #1e293b;
          font-size: 0.68rem;
          font-weight: 700;
        }

        .notification-panel-actions {
          padding: 0.5rem 1rem;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
          align-items: center;
        }

        .notification-action-link {
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: color 0.15s ease, background 0.15s ease;
          text-decoration: none;
          padding: 0.25rem 0.55rem;
          border-radius: 6px;
          border: none;
          background: transparent;
        }

        .mark-read-link {
          color: #0f766e;
        }

        .mark-read-link:hover {
          background: rgba(15, 118, 110, 0.08);
        }

        .delete-all-link {
          color: #94a3b8;
        }

        .delete-all-link:hover {
          color: #dc3545;
          background: rgba(220, 53, 69, 0.06);
        }

        .notification-close-btn {
          background: #f1f5f9;
          border: none;
          color: #64748b;
          font-size: 1.1rem;
          cursor: pointer;
          padding: 0;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: background 0.15s ease, color 0.15s ease;
          line-height: 1;
        }

        .notification-close-btn:hover {
          background: #e2e8f0;
          color: #0f172a;
        }

        .notification-panel-body {
          padding: 0;
          max-height: 360px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f8fafc;
        }

        .notification-panel-body::-webkit-scrollbar {
          width: 4px;
        }

        .notification-panel-body::-webkit-scrollbar-track {
          background: #f8fafc;
        }

        .notification-panel-body::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 999px;
        }

        /* Menu Admin — collé au bouton */
        .admin-menu-container {
          position: relative;
          display: inline-flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .admin-uiverse-dropdown {
          position: absolute;
          top: calc(100% - 1px);
          right: 0;
          z-index: 9999;
          margin: 0;
          animation: none;
        }

        .admin-uiverse-card {
          width: 220px;
          background-color: #fff;
          border-radius: 10px 0 10px 10px;
          padding: 10px 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.1);
          border: 1px solid #d0d5dd;
        }

        .admin-uiverse-list {
          list-style-type: none;
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 0 10px;
          margin: 0;
        }

        .admin-uiverse-element {
          display: flex;
          align-items: center;
          color: #555;
          gap: 10px;
          transition: all 0.25s ease;
          padding: 6px 10px;
          border-radius: 6px;
          cursor: pointer;
        }

        .admin-uiverse-element svg {
          width: 19px;
          height: 19px;
          flex-shrink: 0;
          transition: all 0.3s ease-out;
        }

        .admin-uiverse-label {
          margin: 0;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .admin-uiverse-element:hover {
          background-color: rgba(226, 168, 50, 0.1);
          color: #92700c;
          transform: translateX(2px);
        }

        .admin-uiverse-element:hover svg {
          stroke: #92700c;
        }

        .admin-uiverse-element:active {
          transform: scale(0.99);
        }

        .uiverse-nav-btn--admin .uiverse-nav-admin-chevron {
          font-size: 0.65rem;
          margin-left: 0.35em;
          opacity: 0.85;
          transition: transform 0.2s ease;
        }

        .uiverse-nav-btn--admin.is-open .uiverse-nav-admin-chevron {
          transform: rotate(180deg);
        }
        
        .notification-empty {
          text-align: center;
          color: #94a3b8;
          font-size: 0.84rem;
          margin: 0;
          padding: 2rem 1rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .notification-empty i {
          font-size: 1.5rem;
          color: #cbd5e1;
        }
        
        .notification-item {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #f1f5f9;
          cursor: pointer;
          transition: background 0.15s ease;
          position: relative;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.65rem;
        }

        .notification-item:last-child {
          border-bottom: none;
        }

        .notification-item:hover {
          background: #f8fafc;
        }

        .notification-item.unread {
          background: rgba(15, 118, 110, 0.04);
        }

        .notification-item.unread::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background: linear-gradient(180deg, #0f766e 0%, #115e59 100%);
          border-radius: 0 2px 2px 0;
        }

        .notification-item.unread:hover {
          background: rgba(15, 118, 110, 0.07);
        }

        .notification-content {
          flex: 1;
          min-width: 0;
          padding-left: 0.15rem;
        }

        .notification-message {
          margin: 0 0 0.25rem 0;
          color: #334155;
          font-size: 0.82rem;
          line-height: 1.45;
          font-weight: 500;
        }

        .notification-item.unread .notification-message {
          color: #0f172a;
          font-weight: 600;
        }

        .notification-time {
          color: #94a3b8;
          font-size: 0.72rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }

        .notification-time i {
          font-size: 0.65rem;
          color: #cbd5e1;
        }

        .notification-dot {
          width: 8px;
          height: 8px;
          background: linear-gradient(135deg, #e2a832, #daa520);
          border-radius: 50%;
          margin-top: 5px;
          flex-shrink: 0;
          box-shadow: 0 0 0 2px rgba(226, 168, 50, 0.2);
        }
        
        @media (max-width: 992px) {
          .uiverse-header {
            padding: 0.5rem 0.75rem;
          }
          
          .uiverse-nav-wrap {
            display: none !important;
          }
          
          .uiverse-header .header-brand img {
            height: 28px !important;
          }
        }
        
        @media (max-width: 768px) {
          .uiverse-header {
            padding: 0.5rem 0.75rem;
          }
          
          .uiverse-header .header-brand img {
            height: 28px !important;
          }
          
          .uiverse-btn-logout {
            padding: 4px 10px;
            font-size: 0.7rem;
          }
          
          .uiverse-btn-logout i {
            font-size: 0.75rem;
            margin-right: 4px;
          }
          
          .notification-icon {
            font-size: 1.1rem;
          }
          
          .notification-badge {
            width: 16px;
            height: 16px;
            font-size: 0.6rem;
            top: 1px;
            right: 1px;
          }
          
          .notification-panel {
            width: 300px;
          }
          
          .uiverse-btn-logout span {
            font-size: 0.7rem;
          }
          
          .notification-trigger .notification-badge {
            width: 16px;
            height: 16px;
            font-size: 0.6rem;
            top: -3px;
            right: -3px;
          }
          
          /* Afficher le menu mobile */
          .mobile-menu {
            display: block;
          }
          
          .modern-dropdown {
            min-width: 180px;
            font-size: 0.85rem;
          }
        }
        
        @media (min-width: 769px) and (max-width: 992px) {
          /* Afficher le menu mobile aussi sur tablettes en paysage */
          .mobile-menu {
            display: block;
          }
          
          .mobile-menu-overlay {
            display: none;
          }
          
          .mobile-menu-overlay.show {
            display: block;
          }
        }
        
        @media (max-width: 576px) {
          .uiverse-header {
            padding: 0.4rem 0.5rem;
          }
          
          .uiverse-header .header-brand img {
            height: 24px !important;
          }
          
          .uiverse-btn-logout {
            padding: 3px 8px;
            font-size: 0.65rem;
          }
          
          .uiverse-btn-logout i {
            font-size: 0.7rem;
            margin-right: 3px;
          }
          
          .notification-trigger .notification-icon {
            font-size: 0.8rem;
          }
          
          .uiverse-btn-logout span {
            font-size: 0.65rem;
          }
          
          .notification-trigger .notification-badge {
            width: 14px;
            height: 14px;
            font-size: 0.55rem;
            top: -2px;
            right: -2px;
          }
          
          .mobile-menu {
            width: 85%;
          }
        }

        /* Animations pour l'indicateur LIVE */
        @keyframes shine {
          0% {
            left: -100%;
          }
          50%, 100% {
            left: 100%;
          }
        }

        @keyframes pulse-live {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(255, 0, 0, 0);
            transform: scale(1.1);
          }
        }

        @keyframes pulse-ring {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(2);
            opacity: 0;
          }
        }

        .caisse-live-container:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(40, 167, 69, 0.5), 0 0 30px rgba(40, 167, 69, 0.3);
        }

        .caisse-live-badge {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.25);
          border-radius: 20px;
          padding: 8px 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-left: 20px;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .caisse-live-badge:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .caisse-live-badge i {
          color: #ffd700;
          font-size: 1.1rem;
        }

        .caisse-label {
          color: white;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .caisse-amount {
          color: #ffd700;
          font-weight: 700;
          font-size: 1rem;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        }

        .caisse-loading {
          color: rgba(255, 255, 255, 0.7);
          font-style: italic;
        }

        @media (max-width: 768px) {
          .caisse-live-badge {
            margin-left: 10px;
            padding: 6px 12px;
            font-size: 0.85rem;
          }

          .caisse-label {
            font-size: 0.8rem;
          }

          .caisse-amount {
            font-size: 0.9rem;
          }
        }
      `}</style>

      <nav className="navbar uiverse-header">
          <div className="uiverse-nav-wrap d-none d-md-flex">
              <div
                className={`uiverse-nav-container${isHomeModulePage ? ' uiverse-nav-container--home' : ''}${isNhModulePage ? ' uiverse-nav-container--nh' : ''}`}
              >
                <div className="uiverse-nav-start">
                  <NavPageLink
                    className="uiverse-nav-btn uiverse-nav-brand"
                    to="/"
                    title="Aller à l'accueil"
                  >
                    <img src="/logo.png" alt="Caisse CGM" />
                  </NavPageLink>

                  {isHomeModulePage && (
                    <span className="uiverse-nav-brand-name">Gestion Caisse CGM</span>
                  )}

                  {isNhModulePage && (
                    <button
                      type="button"
                      className="uiverse-nav-module-title uiverse-nav-module-title--nh uiverse-nav-module-title--clickable"
                      onClick={() => navigate('/nh', { state: { pickMatricule: true } })}
                      title="Choisir un matricule comptable"
                    >
                      Gestion NH CGM
                    </button>
                  )}

                  {isCgmCaisseModulePage && (
                    <span className="uiverse-nav-brand-name">Gestion caisse CGM</span>
                  )}

                  {isClientDetailPage && (
                    <button
                      type="button"
                      className="uiverse-nav-btn"
                      onClick={() => {
                        const currentPath = location.pathname;
                        const match = currentPath.match(/\/client\/(\d+)/);
                        if (match) {
                          navigate(`/client/${match[1]}/charges`);
                        }
                      }}
                      title="Retour au tableau des charges"
                    >
                      <i className="fas fa-arrow-left" /> Les charges
                    </button>
                  )}
                </div>

                <div
                  className={`uiverse-nav-links${isHomeModulePage ? ' uiverse-nav-links--home' : ''}${isNhModulePage ? ' uiverse-nav-links--nh' : ''}${isCgmCaisseModulePage ? ' uiverse-nav-links--cgm-bar' : ''}`}
                >

            {/* Boutons admin sur la page d'accueil */}
            {isHomeModulePage && user?.role === 'admin' && (
              <>
                <NavPageLink className="uiverse-nav-btn" to="/comptes" title="Gérer les comptes">
                  <i className="fas fa-users-cog me-2"></i>Gérer les comptes
                </NavPageLink>
                <span className="uiverse-nav-gold-sep" />
                <NavPageLink className="uiverse-nav-btn" to="/dashboard" title="Statistiques">
                  <i className="fas fa-chart-line me-2"></i>Statistiques
                </NavPageLink>
                <span className="uiverse-nav-gold-sep" />
                <div
                  className={`uiverse-nav-dropdown user-settings-container${showUserSettingsPanel ? ' is-open' : ''}`}
                  onMouseLeave={() => setShowUserSettingsPanel(false)}
                >
                  <button
                    className={`uiverse-nav-btn dropdown-toggle${showUserSettingsPanel ? ' is-open' : ''}`}
                    type="button"
                    onMouseEnter={() => { setShowUserSettingsPanel(true); setShowAdminPanel(false); setShowNotificationPanel(false); }}
                    onClick={(e) => { e.stopPropagation(); setShowUserSettingsPanel(!showUserSettingsPanel); }}
                    title="Paramètres"
                    aria-expanded={showUserSettingsPanel}
                    aria-haspopup="true"
                  >
                    <i className="fas fa-cog me-2" aria-hidden="true" />
                    Paramètres
                    <i className="fas fa-chevron-down nav-btn-chevron" aria-hidden="true" />
                  </button>
                  {showUserSettingsPanel && (
                    <ul className="dropdown-menu modern-dropdown show" style={{ display: 'block' }}>
                      <li>
                        <button className="dropdown-item modern-dropdown-item" onClick={() => { setShowUserSettingsPanel(false); navigate('/comptes'); }}>
                          <i className="fas fa-user-edit me-2"></i>Modifier mon compte
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item modern-dropdown-item" onClick={() => setDarkMode(!darkMode)}>
                          <i className={`${darkMode ? 'fas fa-sun' : 'fas fa-moon'} me-2`}></i>
                          Mode sombre
                          <span className={`user-settings-toggle${darkMode ? ' is-on' : ''}`} style={{ marginLeft: 'auto' }}>
                            <span className="user-settings-toggle-knob" />
                          </span>
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              </>
            )}

            {/* Bouton Gérer Client - navigation directe */}
            {isCgmCaisseModulePage && !isClientDetailPage && (
              <>
                <NavPageLink
                  className="uiverse-nav-btn"
                  to="/clients"
                  title="Gérer les clients"
                >
                  <i className="fas fa-users me-2"></i>Gérer Client
                </NavPageLink>
                <span className="uiverse-nav-gold-sep" />
              </>
            )}

            {/* Bouton Honoraires - Cliquer pour aller directement à la page */}
            {isCgmCaisseModulePage && !isClientDetailPage && (
              <>
                <NavPageLink
                  className="uiverse-nav-btn uiverse-nav-btn--honoraires"
                  to="/honoraires/tous"
                  title="Honoraires (j/m/a)"
                >
                  <i className="fas fa-money-bill-wave me-2"></i>Honoraire
                </NavPageLink>
                <span className="uiverse-nav-gold-sep" />
              </>
            )}

            {/* Bouton Client - Regroupe Dépenses Client et État Client pour Admin */}
            {isCgmCaisseModulePage && !isClientDetailPage && user?.role === 'admin' && (
              <div
                className={`uiverse-nav-dropdown dropdown${showClientDropdown ? ' is-open' : ''}`}
                data-nav-menu="client"
                onMouseEnter={() => {
                  setShowClientDropdown(true);
                  setShowCgmDropdown(false);
                }}
                onMouseLeave={() => setShowClientDropdown(false)}
              >
                <button
                  className={`uiverse-nav-btn dropdown-toggle${showClientDropdown ? ' is-open' : ''}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowClientDropdown((v) => !v);
                    setShowCgmDropdown(false);
                  }}
                  aria-expanded={showClientDropdown}
                  aria-haspopup="true"
                >
                  <i className="fas fa-users me-2" aria-hidden="true" />
                  Client
                  <i className="fas fa-chevron-down nav-btn-chevron" aria-hidden="true" />
                </button>
                <ul className={`dropdown-menu modern-dropdown ${showClientDropdown ? 'show' : ''}`}>
                  {/* Section Dépenses Client */}
                  <li>
                    <h6 className="dropdown-header modern-dropdown-header">
                      <i className="fas fa-credit-card me-2"></i>Dépenses Client
                    </h6>
                  </li>
                  {/* Liens Par jour/mois/année supprimés pour Dépenses Client (navbar admin) */}
                  <li>
                    <button
                      className="dropdown-item modern-dropdown-item"
                      onClick={() => handleAdminDepensesClientClick('voir-toutes')}
                    >
                      <i className="fas fa-list me-2"></i>Dépenses (j/m/a)
                    </button>
                  </li>

                  <li><hr className="dropdown-divider" /></li>

                  {/* Section État Client */}
                  <li>
                    <h6 className="dropdown-header modern-dropdown-header">
                      <i className="fas fa-chart-line me-2"></i>État Client
                    </h6>
                  </li>
                  {/* Boutons Par jour/mois/année supprimés pour État Client (navbar admin) */}
                  <li>
                    <button
                      className="dropdown-item modern-dropdown-item"
                      onClick={() => handleAdminEtatClientClick('voir-toutes')}
                    >
                      <i className="fas fa-list me-2"></i>Voir toutes les états
                    </button>
                  </li>
                </ul>
              </div>
            )}
            {isCgmCaisseModulePage && !isClientDetailPage && user?.role === 'admin' && (
              <span className="uiverse-nav-gold-sep" />
            )}

            {/* Honoraires reçus - visible dans la page détail client - navigation directe */}
            {isClientDetailPage && (
              <>
                <button
                  className="uiverse-nav-btn"
                  type="button"
                  onClick={() => handleHonorairesClick('toutes')}
                  title="Honoraires (j/m/a)"
                >
                  <i className="fas fa-money-bill-wave me-2"></i>Honoraires (j/m/a)
                </button>
                <span className="uiverse-nav-gold-sep" />
              </>
            )}

            {/* Bouton Dépenses Client - visible uniquement dans la page détail client - navigation directe */}
            {isClientDetailPage && (
              <>
                <button
                  className="uiverse-nav-btn"
                  type="button"
                  onClick={() => handleDepensesClick('voir-toutes')}
                  title="Dépenses (j/m/a)"
                >
                  <i className="fas fa-credit-card me-2"></i>Dépenses Client
                </button>
                <span className="uiverse-nav-gold-sep" />
              </>
            )}


            {/* Bouton CGM - Regroupe Dépenses Bureau et État Bureau */}
            {isCgmCaisseModulePage && !isClientDetailPage && (
              <div
                className={`uiverse-nav-dropdown dropdown${showCgmDropdown ? ' is-open' : ''}`}
                data-nav-menu="cgm"
                onMouseEnter={() => {
                  setShowCgmDropdown(true);
                  setShowClientDropdown(false);
                }}
                onMouseLeave={() => setShowCgmDropdown(false)}
              >
                <button
                  className={`uiverse-nav-btn uiverse-nav-btn--cgm dropdown-toggle${showCgmDropdown ? ' is-open' : ''}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCgmDropdown((v) => !v);
                    setShowClientDropdown(false);
                  }}
                  aria-expanded={showCgmDropdown}
                  aria-haspopup="true"
                >
                  <i className="fas fa-building me-2" aria-hidden="true" />
                  {cgmNavLabel}
                  <i className="fas fa-chevron-down nav-btn-chevron" aria-hidden="true" />
                </button>
                <ul className={`dropdown-menu modern-dropdown ${showCgmDropdown ? 'show' : ''}`}>
                  {/* Section Dépenses Bureau */}
                  <li>
                    <h6 className="dropdown-header modern-dropdown-header">
                      <i className="fas fa-credit-card me-2"></i>Dépenses {cgmNavLabel}
                    </h6>
                  </li>
                  <li>
                    <button
                      className="dropdown-item modern-dropdown-item"
                      onClick={() => handleAddDepenseClick()}
                    >
                      <i className="fas fa-list me-2"></i>Dépenses {cgmNavLabel}
                    </button>
                  </li>

                  <li><hr className="dropdown-divider" /></li>

                  {/* Section État Bureau */}
                  <li>
                    <h6 className="dropdown-header modern-dropdown-header">
                      <i className="fas fa-chart-line me-2"></i>État {cgmNavLabel}
                    </h6>
                  </li>
                  <li>

                    <button
                      className="dropdown-item modern-dropdown-item"
                      onClick={handleAfficherParAnnee}
                    >
                      <i className="fas fa-calendar me-2"></i>Etat {cgmNavLabel}
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item modern-dropdown-item"
                      onClick={handleFondClientChezCgm}
                    >
                      <i className="fas fa-wallet me-2"></i>Fond client chez {cgmNavLabel}
                    </button>
                  </li>

                </ul>
              </div>
            )}
            {isCgmCaisseModulePage && !isClientDetailPage && (
              <span className="uiverse-nav-gold-sep" />
            )}

            {/* Bouton Etat Client - visible uniquement dans la page détail client */}
            {isClientDetailPage && (
              <button
                className="uiverse-nav-btn"
                type="button"
                onClick={() => handleClientDetailEtatFilterClick('annee')}
              >
                <i className="fas fa-chart-line me-2"></i>Etat (j/m/a)
              </button>
            )}

            {/* Caisse CGM / CARTE / Statistiques dans la navbar */}
            {(user?.role === 'admin' || user?.role === 'caissier') && isAuthenticated && isCgmCaisseModulePage && !isClientDetailPage && (
              <>
                <NavPageLink
                  className="uiverse-nav-btn"
                  to="/caisse-cgm"
                  title="Gérer la caisse CGM"
                >
                  <i className="fas fa-wallet me-2"></i>Retrait {cgmNavLabel}
                </NavPageLink>
                {isSuperAdmin() && (
                  <>
                    <span className="uiverse-nav-gold-sep" />
                    <NavPageLink
                      className="uiverse-nav-btn"
                      to="/cartes-bancaires"
                      title="Suivi carte bancaire"
                    >
                      <i className="fas fa-credit-card me-2"></i>Carte
                    </NavPageLink>
                    <span className="uiverse-nav-gold-sep" />
                    <NavPageLink
                      className="uiverse-nav-btn"
                      to="/tracabilite"
                      title="Traçabilité"
                    >
                      <i className="fas fa-route me-2" aria-hidden="true" />
                      traçabilité
                    </NavPageLink>
                  </>
                )}
                {canAccessArchive && (
                  <>
                    <span className="uiverse-nav-gold-sep" />
                    <NavPageLink
                      className="uiverse-nav-btn"
                      to="/archives"
                      title="Archives"
                    >
                      <i className="fas fa-archive me-2" aria-hidden="true" />
                      Archive
                    </NavPageLink>
                  </>
                )}
              </>
            )}

                </div>

                <div className="uiverse-nav-end">
                  {isAuthenticated && user && (
                    <input
                      ref={profileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleProfilePhotoUpload}
                    />
                  )}

                  {isAuthenticated && user?.role === 'admin' && (
                    <div className="notification-container">
                      <button
                        type="button"
                        className="uiverse-nav-icon-btn uiverse-nav-btn--notify"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAdminPanel(false);
                          setShowNotificationPanel(!showNotificationPanel);
                          if (!showNotificationPanel) {
                            fetchNotifications();
                          }
                        }}
                        title="Notifications"
                        aria-label="Notifications"
                      >
                        <svg
                          className="uiverse-nav-notify-svg"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
                            fill="currentColor"
                          />
                        </svg>
                        {unreadCount > 0 && (
                          <span className="notification-badge">{unreadCount}</span>
                        )}
                      </button>
                      {showNotificationPanel && (
                        <>
                          <div
                            className="notification-overlay"
                            onClick={() => setShowNotificationPanel(false)}
                          />
                          <div className="notification-panel">
                            <div className="notification-panel-header">
                              <h6>
                                <i className="fas fa-bell" aria-hidden="true" />
                                Notifications
                                {unreadCount > 0 && (
                                  <span className="notification-panel-count">{unreadCount}</span>
                                )}
                              </h6>
                              <button
                                type="button"
                                className="notification-close-btn"
                                onClick={() => setShowNotificationPanel(false)}
                                aria-label="Fermer"
                              >
                                ×
                              </button>
                            </div>
                            {notifications.length > 0 && (
                              <div className="notification-panel-actions">
                                <span
                                  className="notification-action-link mark-read-link"
                                  onClick={async () => {
                                    try {
                                      const response = await fetch('/api/notifications/mark-all-read', { method: 'PUT' });
                                      if (response.ok) fetchNotifications();
                                    } catch (err) {
                                      console.error('Erreur lors du marquage des notifications:', err);
                                    }
                                  }}
                                >
                                  Marquer comme lu tout
                                </span>
                                <span
                                  className="notification-action-link delete-all-link"
                                  onClick={async () => {
                                    if (window.confirm('Êtes-vous sûr de vouloir supprimer toutes les notifications ?')) {
                                      try {
                                        const response = await fetch('/api/notifications/delete-all', { method: 'DELETE' });
                                        if (response.ok) fetchNotifications();
                                      } catch (err) {
                                        console.error('Erreur lors de la suppression des notifications:', err);
                                      }
                                    }
                                  }}
                                >
                                  Supprimer tout
                                </span>
                              </div>
                            )}
                            <div className="notification-panel-body">
                              {notifications.length === 0 ? (
                                <p className="notification-empty">
                                  <i className="far fa-bell-slash" aria-hidden="true" />
                                  Aucune notification
                                </p>
                              ) : (
                                notifications.map((notification) => (
                                  <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!notification.is_read) {
                                        try {
                                          await fetch(`/api/notifications/${notification.id}/read`, { method: 'PUT' });
                                          fetchNotifications();
                                        } catch (err) {
                                          console.error('Erreur lors du marquage de la notification:', err);
                                        }
                                      }
                                      if (notification.client_nom && user?.role === 'admin') {
                                        try {
                                          const clientsResponse = await fetch('/api/clients');
                                          const clientsData = await clientsResponse.json();
                                          if (clientsData?.clients) {
                                            const client = clientsData.clients.find((c) => {
                                              const clientNom = (c.nom || '').trim();
                                              const notificationNom = (notification.client_nom || '').trim();
                                              return clientNom.toLowerCase() === notificationNom.toLowerCase()
                                                || clientNom.toLowerCase().includes(notificationNom.toLowerCase())
                                                || notificationNom.toLowerCase().includes(clientNom.toLowerCase());
                                            });
                                            if (client?.id) {
                                              setShowNotificationPanel(false);
                                              navigate(`/client/${client.id}/charges`);
                                            }
                                          }
                                        } catch (err) {
                                          console.error('Erreur lors de la recherche du client:', err);
                                        }
                                      }
                                    }}
                                    style={{ cursor: user?.role === 'admin' && notification.client_nom ? 'pointer' : 'default' }}
                                  >
                                    <div className="notification-content">
                                      <p className="notification-message">{notification.message}</p>
                                      <span className="notification-time">
                                        <i className="far fa-clock" aria-hidden="true" />
                                        {new Date(notification.created_at).toLocaleString('fr-FR', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}
                                      </span>
                                    </div>
                                    {!notification.is_read && <div className="notification-dot" />}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {isAuthenticated && user ? (
                    <div className="cabinet-nav-menu" data-cabinet-menu>
                      <div className="cabinet-nav-menu__stack cabinet-nav-menu__stack--static" title="Connecté">
                        <span className="cabinet-nav-menu__stack-user">
                          <span className="uiverse-nav-profile-name">
                            {(() => {
                              const displayName = String(user.username || user.nom || 'Utilisateur');
                              return displayName.charAt(0).toUpperCase() + displayName.slice(1);
                            })()}
                          </span>
                          <span className="uiverse-nav-profile-dot" aria-label="Connecté" title="Connecté" />
                        </span>
                        {activeCabinet ? (
                          <span className="cabinet-nav-menu__stack-cabinet">
                            <i className="fas fa-building" aria-hidden="true" /> {activeCabinet.name}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {isAuthenticated && (
                    <button
                      type="button"
                      className="uiverse-nav-btn uiverse-nav-btn--danger"
                      onClick={handleLogout}
                      title="Se déconnecter"
                    >
                      <i className="fas fa-sign-out-alt" /> Logout
                    </button>
                  )}
                </div>

              </div>
          </div>

        <div className="header-row header-row--mobile d-md-none">
          <div className="header-brand">
            <button
              type="button"
              className="header-brand-logo-btn"
              onClick={() => navigate('/')}
              title="Aller à l'accueil"
            >
              <img src="/logo.png" alt="Caisse CGM" />
            </button>
            {isCgmCaisseModulePage && (
              <span className="uiverse-nav-brand-name">CCGM</span>
            )}
            {isHomeModulePage && (
              <span className="uiverse-nav-brand-name">CCGM</span>
            )}
            {isAuthenticated && user?.role === 'admin' && !isHomeModulePage ? (
              <div className="admin-menu-container mobile-header-admin">
                <button
                  type="button"
                  className={`mobile-header-admin-btn${showAdminPanel ? ' is-open' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAdminPanel(!showAdminPanel);
                    setShowNotificationPanel(false);
                    setShowMobileMenu(false);
                  }}
                  aria-expanded={showAdminPanel}
                  aria-haspopup="true"
                  title="Menu administrateur"
                >
                  <i className="fas fa-user-shield" aria-hidden="true" />
                  Admin
                  <i className="fas fa-chevron-down mobile-header-admin-chevron" aria-hidden="true" />
                </button>
                {renderAdminMenuPanel('admin-uiverse-dropdown--mobile')}
              </div>
            ) : isAuthenticated && user && !isHomeModulePage ? (
              <span className="header-brand-username text-light fw-semibold">
                {user.username || user.nom || 'Utilisateur'}
              </span>
            ) : null}
          </div>

          <div className="uiverse-header-actions uiverse-header-actions--mobile d-md-none">
            {isAuthenticated && user?.role === 'admin' && (
              <div className="notification-container">
                <div
                  className="notification-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAdminPanel(false);
                    setShowNotificationPanel(!showNotificationPanel);
                    if (!showNotificationPanel) {
                      fetchNotifications();
                    }
                  }}
                  title="Notifications"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowAdminPanel(false);
                      setShowNotificationPanel(!showNotificationPanel);
                      if (!showNotificationPanel) fetchNotifications();
                    }
                  }}
                >
                  <svg
                    className="uiverse-nav-notify-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    style={{ width: 18, height: 18 }}
                  >
                    <path
                      d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
                      fill="currentColor"
                    />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </div>
                
                {/* Fenêtre de notification */}
                {showNotificationPanel && (
                  <>
                    <div 
                      className="notification-overlay"
                      onClick={() => setShowNotificationPanel(false)}
                    ></div>
                    <div className="notification-panel">
                      <div className="notification-panel-header">
                        <h6>
                          <i className="fas fa-bell" aria-hidden="true" />
                          Notifications
                          {unreadCount > 0 && (
                            <span className="notification-panel-count">{unreadCount}</span>
                          )}
                        </h6>
                        <button 
                          type="button"
                          className="notification-close-btn"
                          onClick={() => setShowNotificationPanel(false)}
                          aria-label="Fermer"
                        >
                          ×
                        </button>
                      </div>
                      {notifications.length > 0 && (
                        <div className="notification-panel-actions">
                          <span
                            className="notification-action-link mark-read-link"
                            onClick={async () => {
                              try {
                                const response = await fetch('/api/notifications/mark-all-read', {
                                  method: 'PUT'
                                });
                                if (response.ok) {
                                  fetchNotifications();
                                }
                              } catch (err) {
                                console.error('Erreur lors du marquage des notifications:', err);
                              }
                            }}
                            title="Marquer toutes comme lues"
                          >
                            Marquer comme lu tout
                          </span>
                          <span
                            className="notification-action-link delete-all-link"
                            onClick={async () => {
                              if (window.confirm('Êtes-vous sûr de vouloir supprimer toutes les notifications ?')) {
                                try {
                                  const response = await fetch('/api/notifications/delete-all', {
                                    method: 'DELETE'
                                  });
                                  if (response.ok) {
                                    fetchNotifications();
                                  }
                                } catch (err) {
                                  console.error('Erreur lors de la suppression des notifications:', err);
                                }
                              }
                            }}
                            title="Supprimer toutes les notifications"
                          >
                            Supprimer tout
                          </span>
                        </div>
                      )}
                      <div className="notification-panel-body">
                        {notifications.length === 0 ? (
                          <p className="notification-empty">
                            <i className="far fa-bell-slash" aria-hidden="true" />
                            Aucune notification
                          </p>
                        ) : (
                          <>
                            {notifications.map((notification) => (
                              <div 
                                key={notification.id} 
                                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  
                                  // Marquer comme lu si non lu
                                  if (!notification.is_read) {
                                    try {
                                      await fetch(`/api/notifications/${notification.id}/read`, {
                                        method: 'PUT'
                                      });
                                      fetchNotifications();
                                    } catch (err) {
                                      console.error('Erreur lors du marquage de la notification:', err);
                                    }
                                  }

                                  // Si la notification contient des informations sur un client, naviguer vers ses charges
                                  if (notification.client_nom && user?.role === 'admin') {
                                    try {
                                      // Rechercher le client par nom
                                      const clientsResponse = await fetch('/api/clients');
                                      const clientsData = await clientsResponse.json();
                                      
                                      if (clientsData && clientsData.clients) {
                                        // Chercher le client qui correspond au nom dans la notification
                                        const client = clientsData.clients.find(c => {
                                          const clientNom = (c.nom || '').trim();
                                          const notificationNom = (notification.client_nom || '').trim();
                                          
                                          // Comparaison flexible (insensible à la casse et aux espaces)
                                          return clientNom.toLowerCase() === notificationNom.toLowerCase() ||
                                                 clientNom.toLowerCase().includes(notificationNom.toLowerCase()) ||
                                                 notificationNom.toLowerCase().includes(clientNom.toLowerCase());
                                        });
                                        
                                        if (client && client.id) {
                                          // Fermer le panneau de notifications
                                          setShowNotificationPanel(false);
                                          
                                          // Naviguer vers les charges du client
                                          navigate(`/client/${client.id}/charges`);
                                        } else {
                                          console.warn('Client non trouvé pour la notification:', notification.client_nom);
                                        }
                                      }
                                    } catch (err) {
                                      console.error('Erreur lors de la recherche du client:', err);
                                    }
                                  }
                                }}
                                style={{ cursor: user?.role === 'admin' && notification.client_nom ? 'pointer' : 'default' }}
                              >
                                <div className="notification-content">
                                  <p className="notification-message">{notification.message}</p>
                                  <span className="notification-time">
                                    <i className="far fa-clock" aria-hidden="true" />
                                    {new Date(notification.created_at).toLocaleString('fr-FR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                                {!notification.is_read && <div className="notification-dot"></div>}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <label
              className="burger mobile-menu-burger d-lg-none"
              htmlFor="mobile-nav-burger"
              title={showMobileMenu ? 'Fermer le menu' : 'Ouvrir le menu'}
            >
              <input
                type="checkbox"
                id="mobile-nav-burger"
                checked={showMobileMenu}
                onChange={(e) => setShowMobileMenu(e.target.checked)}
                aria-label="Menu de navigation"
                aria-expanded={showMobileMenu}
              />
              <span />
              <span />
              <span />
            </label>
          </div>
        </div>

        {/* Overlay pour fermer le menu mobile */}
        {showMobileMenu && (
          <div 
            className="mobile-menu-overlay show"
            onClick={() => setShowMobileMenu(false)}
          ></div>
        )}

        {/* Menu mobile */}
        <div className={`mobile-menu ${showMobileMenu ? 'show' : ''}`}>
          <div className="mobile-menu-header">
            <div className="mobile-menu-title">
              <img src="/logo.png" alt="" />
              CCGM
            </div>
            <button
              className="mobile-menu-close"
              onClick={() => setShowMobileMenu(false)}
              aria-label="Fermer le menu"
            >
              <span>×</span>
            </button>
          </div>

          <div className="mobile-menu-content">
            {isAuthenticated && user ? (
              <div className="mobile-menu-user">
                <span className="mobile-menu-user__avatar" aria-hidden="true">
                  {String(user.username || user.nom || 'U')
                    .charAt(0)
                    .toUpperCase()}
                </span>
                <span className="mobile-menu-user__meta">
                  <span className="mobile-menu-user__name">
                    {user.username || user.nom || 'Utilisateur'}
                  </span>
                  {activeCabinet ? (
                    <span className="mobile-menu-user__cab">
                      Cabinet {activeCabinet.code || activeCabinet.name}
                    </span>
                  ) : null}
                </span>
              </div>
            ) : null}

            {/* Bouton retour vers tableau des charges - visible uniquement dans les détails client (mobile) */}
            {isClientDetailPage && (
              <div className="mobile-menu-section">
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    const currentPath = location.pathname;
                    const match = currentPath.match(/\/client\/(\d+)/);
                    if (match) {
                      const clientId = match[1];
                      navigate(`/client/${clientId}/charges`);
                      setShowMobileMenu(false);
                    }
                  }}
                >
                  <i className="fas fa-arrow-left"></i>Les charges
                </button>
              </div>
            )}

            {/* Accueil modules — mêmes actions que PC */}
            {isHomeModulePage && !isClientDetailPage && (
              <div className="mobile-menu-section">
                <div className="mobile-menu-section-title">Navigation</div>
                {user && user.role === 'admin' && (
                  <>
                    <button
                      className="mobile-menu-item"
                      onClick={() => {
                        navigate('/comptes');
                        setShowMobileMenu(false);
                      }}
                    >
                      <i className="fas fa-user-cog"></i>Gérer les comptes
                    </button>
                    <button
                      className="mobile-menu-item"
                      onClick={() => {
                        navigate('/dashboard');
                        setShowMobileMenu(false);
                      }}
                    >
                      <i className="fas fa-chart-bar"></i>Statistiques
                    </button>
                    <button
                      className="mobile-menu-item"
                      onClick={() => {
                        navigate('/comptes');
                        setShowMobileMenu(false);
                      }}
                    >
                      <i className="fas fa-cog"></i>Paramètres
                    </button>
                  </>
                )}
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    navigate('/cabinets');
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="fas fa-building"></i>Changer de cabinet
                </button>
              </div>
            )}

            {/* Section principale - Navigation générale (hors page modules) */}
            {!isClientDetailPage && !isHomeModulePage && (
              <div className="mobile-menu-section">
                <div className="mobile-menu-section-title">Navigation</div>
                
                {/* Bouton Gérer les comptes - Visible uniquement pour les admins */}
                {user && user.role === 'admin' && (
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      navigate('/comptes');
                      setShowMobileMenu(false);
                    }}
                  >
                    <i className="fas fa-user-cog"></i>Gérer les comptes
                  </button>
                )}

                {/* Bouton Gérer Client */}
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    navigate('/clients');
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="fas fa-users"></i>Gérer Client
                </button>

                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    handleHonorairesClick('toutes');
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="fas fa-money-bill-wave"></i>Honoraire
                </button>
              </div>
            )}

            {/* Section Client (Admin) */}
            {isCgmCaisseModulePage && !isClientDetailPage && user?.role === 'admin' && (
              <div className="mobile-menu-section">
                <div className="mobile-menu-section-title">Client</div>
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    handleAdminDepensesClientClick('voir-toutes');
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="fas fa-list"></i>Dépenses (j/m/a)
                </button>
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    handleAdminEtatClientClick('voir-toutes');
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="fas fa-list"></i>Voir toutes les états
                </button>
              </div>
            )}

            {/* Section CGM */}
            {isCgmCaisseModulePage && !isClientDetailPage && (
              <div className="mobile-menu-section">
                <div className="mobile-menu-section-title">{cgmNavLabel}</div>
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    handleAddDepenseClick();
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="fas fa-list"></i>Dépenses {cgmNavLabel}
                </button>
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    handleAfficherParAnnee();
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="fas fa-calendar"></i>Etat (j/m/a)
                </button>
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    handleFondClientChezCgm();
                    setShowMobileMenu(false);
                  }}
                >
                  <i className="fas fa-wallet"></i>Fond client chez {cgmNavLabel}
                </button>
              </div>
            )}

            {/* Section Client Detail */}
            {isClientDetailPage && (
              <>
                <div className="mobile-menu-section">
                  <div className="mobile-menu-section-title">Client</div>
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      handleHonorairesClick('toutes');
                      setShowMobileMenu(false);
                    }}
                  >
                    <i className="fas fa-money-bill-wave"></i>Honoraires (j/m/a)
                  </button>
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      handleDepensesClick('voir-toutes');
                      setShowMobileMenu(false);
                    }}
                  >
                    <i className="fas fa-credit-card"></i>Dépenses Client
                  </button>
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      handleClientDetailEtatFilterClick('annee');
                      setShowMobileMenu(false);
                    }}
                  >
                    <i className="fas fa-chart-line"></i>Etat (j/m/a)
                  </button>
                </div>
              </>
            )}

            {/* Section Caisse et Outils */}
            {(user?.role === 'admin' || user?.role === 'caissier') && isAuthenticated && isCgmCaisseModulePage && !isClientDetailPage && (
              <div className="mobile-menu-section">
                <div className="mobile-menu-section-title">Outils</div>
                <NavPageLink
                  className="mobile-menu-item"
                  to="/caisse-cgm"
                  onNavigate={() => {
                    setShowMobileMenu(false);
                    navigate('/caisse-cgm');
                  }}
                >
                  <i className="fas fa-wallet"></i>Retrait {cgmNavLabel}
                </NavPageLink>
                {isSuperAdmin() && (
                  <>
                    <NavPageLink
                      className="mobile-menu-item"
                      to="/cartes-bancaires"
                      onNavigate={() => {
                        setShowMobileMenu(false);
                        navigate('/cartes-bancaires');
                      }}
                    >
                      <i className="fas fa-credit-card"></i>Carte
                    </NavPageLink>
                    <NavPageLink
                      className="mobile-menu-item"
                      to="/tracabilite"
                      onNavigate={() => {
                        setShowMobileMenu(false);
                        navigate('/tracabilite');
                      }}
                    >
                      <i className="fas fa-route"></i>traçabilité
                    </NavPageLink>
                  </>
                )}
                {user?.role === 'admin' && (
                  <>
                    <button
                      className="mobile-menu-item"
                      onClick={() => {
                        // Fonctionnalité à implémenter plus tard
                        console.log('Notifications clicked');
                        setShowMobileMenu(false);
                      }}
                    >
                      <span style={{ marginRight: '12px', fontSize: '1rem' }}>🔔</span>Notifications
                    </button>
                    <button
                      className="mobile-menu-item"
                      onClick={() => {
                        navigate('/dashboard');
                        setShowMobileMenu(false);
                      }}
                    >
                      <i className="fas fa-chart-line"></i>Statistiques
                    </button>
                  </>
                )}
              </div>
            )}

            {isAuthenticated && (
              <div className="mobile-menu-section">
                <div className="mobile-menu-section-title">Compte</div>
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setDarkMode(!darkMode);
                  }}
                >
                  <i className={`fas ${darkMode ? 'fa-sun' : 'fa-moon'}`}></i>
                  {darkMode ? 'Mode clair' : 'Mode sombre'}
                </button>
                <button
                  className="mobile-menu-item mobile-menu-item--danger"
                  onClick={() => {
                    setShowMobileMenu(false);
                    handleLogout();
                  }}
                >
                  <i className="fas fa-sign-out-alt"></i>Déconnexion
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Modal des honoraires */}
        <HonorairesModal
          show={showHonorairesModal}
          onClose={() => setShowHonorairesModal(false)}
          type={honorairesType}
        />

        {/* Modal des dépenses */}
        <DepensesModal
          show={showDepensesModal}
          onClose={() => setShowDepensesModal(false)}
          action={depensesAction}
        />

        {/* Modal de filtrage des dépenses */}
        <DepensesFilterModal
          show={showDepensesFilterModal}
          onClose={() => setShowDepensesFilterModal(false)}
          type={depensesFilterType}
        />

        {/* Modal de toutes les honoraires */}
        <AllHonorairesModal
          show={showAllHonorairesModal}
          onClose={() => setShowAllHonorairesModal(false)}
        />

        {/* Modal de toutes les dépenses */}
        <AllDepensesModal
          show={showAllDepensesModal}
          onClose={() => setShowAllDepensesModal(false)}
          actionMode={depensesActionMode}
        />

        {/* Modal de tous les clients */}
        <AllClientsModal
          show={showAllClientsModal}
          onClose={() => setShowAllClientsModal(false)}
        />

        {/* Modal de tous les bénéficiaires */}
        <AllBeneficiairesModal
          show={showAllBeneficiairesModal}
          onClose={() => setShowAllBeneficiairesModal(false)}
        />

        {/* Gabarit de reçu */}
        <RecuTemplate
          show={showRecuTemplate}
          onClose={() => setShowRecuTemplate(false)}
          data={recuData}
        />

        {/* Modal Etat CGM */}
        <EtatCgmModal
          show={showEtatCgmModal}
          onClose={() => setShowEtatCgmModal(false)}
          filteredData={etatCgmFilteredData}
        />

        {/* Modal Gestion des utilisateurs (désactivé, page dédiée) */}


      </nav>
    </>
  );
}